import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * Everything the app knows about being installed, updated, and online.
 *
 * Three separate concerns end up here because they are all answers to the same
 * question — "what is the shape of this session?" — and because each of them
 * needs exactly one listener for the whole app. A component that mounts a
 * second `beforeinstallprompt` handler does not get a second event; the browser
 * fires it once.
 */

interface PWAState {
  /** A newer build is downloaded and waiting for permission to take over. */
  updateReady: boolean;
  /** Swap to the waiting build and reload. */
  applyUpdate: () => void;
  /** Leave the current build alone; the update applies on the next cold start. */
  dismissUpdate: () => void;

  /** The shell is cached — the app will open with no connection. */
  offlineReady: boolean;
  dismissOfflineReady: () => void;

  /** `navigator.onLine`, kept current. */
  online: boolean;

  /** The browser has offered an install and we are holding the event. */
  canInstall: boolean;
  /** Show the browser's install dialog. Resolves to whether it was accepted. */
  promptInstall: () => Promise<boolean>;
  /** Already running from the home screen / app drawer. */
  isStandalone: boolean;
}

const PWAContext = createContext<PWAState | null>(null);

/**
 * The `beforeinstallprompt` event, which TypeScript's DOM lib does not describe
 * because it is Chromium-only.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    // iOS Safari predates the display-mode query and uses its own flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const {
    needRefresh: [updateReady, setUpdateReady],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(url, registration) {
      if (!registration) return;
      /*
        Browsers only check for a new worker on navigation, and a standalone PWA
        that is never closed may not navigate for days. An hourly check means a
        deploy reaches a long-lived session the same day rather than whenever
        the guest happens to cold-start the app.
      */
      const HOUR = 60 * 60 * 1000;
      const id = window.setInterval(() => {
        if (navigator.onLine) void registration.update();
      }, HOUR);
      window.addEventListener("beforeunload", () => window.clearInterval(id), { once: true });
    },
    onRegisterError(error) {
      console.warn("[pwa] service worker registration failed", error);
    },
  });

  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      /*
        Suppressing the browser's own mini-infobar is the whole point of
        capturing this event: it lets the app ask at a moment that means
        something — after a first search, not two seconds into a cold visit.
      */
      event.preventDefault();
      deferredPrompt.current = event as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    const onInstalled = () => {
      deferredPrompt.current = null;
      setCanInstall(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    const displayMode = window.matchMedia("(display-mode: standalone)");
    const onDisplayChange = () => setIsStandalone(detectStandalone());
    displayMode.addEventListener("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      displayMode.removeEventListener("change", onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferredPrompt.current;
    if (!event) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    /*
      The event is single-use. Whatever the answer, it cannot be shown again
      until the browser decides to fire a fresh one.
    */
    deferredPrompt.current = null;
    setCanInstall(false);
    return outcome === "accepted";
  }, []);

  const applyUpdate = useCallback(() => {
    void updateServiceWorker(true);
  }, [updateServiceWorker]);

  const value = useMemo<PWAState>(
    () => ({
      updateReady,
      applyUpdate,
      dismissUpdate: () => setUpdateReady(false),
      offlineReady,
      dismissOfflineReady: () => setOfflineReady(false),
      online,
      canInstall,
      promptInstall,
      isStandalone,
    }),
    [
      updateReady,
      applyUpdate,
      setUpdateReady,
      offlineReady,
      setOfflineReady,
      online,
      canInstall,
      promptInstall,
      isStandalone,
    ],
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWA(): PWAState {
  const ctx = useContext(PWAContext);
  if (!ctx) throw new Error("usePWA must be used inside PWAProvider");
  return ctx;
}

/**
 * Drop every Cache Storage entry this app owns.
 *
 * Called on sign-out. The catalogue cache is public data, but a shared phone is
 * a real thing and "log out" should mean the next person sees nothing of the
 * last one — including the listings they were looking at.
 *
 * The precache is left alone: it is the app itself, it contains nothing about
 * anybody, and clearing it would turn the next launch into a cold download.
 */
export async function clearUserCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  const names = await caches.keys();
  await Promise.all(
    names
      .filter((name) => name === "api-catalogue" || name === "listing-images")
      .map((name) => caches.delete(name)),
  );
}
